import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Drawer, DrawerProps, message } from 'antd';
import useSecondConversation from 'hooks/business/useSecondConversation';
import { useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import Chat from 'views/Conversation/Chat';
import TodoSider from './TodoSider';
import useCrud from 'hooks/useCrud';
import { GetTalkInfoByBuyerStoreBody, getTalkInfoByBuyerStore } from 'services/api/refund';
import { ChatConversationProvider } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { useSetConversation } from 'hooks/business/lruConversation';
import { ChatErrorBoundary } from 'views/Conversation/components/ChatSection';
import { createConversation } from 'services/api/refund';
import { addFakeConversationOrderSn } from 'hooks/business/conversation';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { ClassicConversationHead } from 'views/Conversation/Chat/components/ClassicConversationHead';
import { css } from '@emotion/css';
import { LightChatHeader } from 'components/LightConversation/LightChatHeader';

export interface TodoDrawerProps extends DrawerProps {
  todo: Todo.Record;
  readonly?: boolean;
  onUpdate?: () => void;
}

function TodoDrawer(props: TodoDrawerProps) {
  const { todo, readonly, onUpdate, visible, ...otherProps } = props;

  const { platform, region, tradeBuyerId, externalStoreId, relatedTradeId } = todo;

  const { t } = useTranslate();

  const setConversation = useSetConversation();

  const { item, loading, fetch } = useCrud({
    read: (params: GetTalkInfoByBuyerStoreBody['items'][0]) =>
      getTalkInfoByBuyerStore({
        items: [params],
        needPhone: true,
      }).then((res) => {
        const conversation = res.talkInfos[0];

        if (
          conversation &&
          conversation.canBeSent &&
          conversation.talkId !== '0' &&
          (conversation.controlStatus || conversation.receptionId !== '0')
        ) {
          setConversation(conversation.conversationId, conversation);
          return conversation;
        } else {
          return createConversation({
            externalBuyerId: tradeBuyerId,
            channel: platform,
            region,
            sourceAction: 'TODO',
            externalStoreId: externalStoreId,
            orderId: relatedTradeId,
          }).then((res) => {
            const { conversationId } = res;
            setConversation(conversationId, res);
            if (res.mockExternal && res.channel === 'SHOPEE' && (todo as any).relatedTradeId) {
              addFakeConversationOrderSn(res.conversationId, (todo as any).relatedTradeId);
            }
            return res;
          });
        }
      }),
  });

  const refsCurrent = { fetch, t };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;
  const isDaren = todo?.type === 12;

  useEffect(() => {
    refs.current
      .fetch({
        channel: platform,
        region,
        externalBuyerId: isDaren ? `${tradeBuyerId}SA` : tradeBuyerId,
        externalStoreId: externalStoreId,
      })
      .catch(() => {
        message.error(refs.current.t('phrase.获取数据失败，请稍后再试'));
      });
  }, [platform, region, tradeBuyerId, externalStoreId, isDaren]);

  return (
    <Drawer
      push={false}
      title={t('todo.待办详情')}
      width={1200}
      contentWrapperStyle={{ maxWidth: '100vw' }}
      bodyStyle={{ padding: 0 }}
      visible={visible}
      {...otherProps}
    >
      {item ? (
        <DrawerContent
          key={item.conversationId}
          readonly={readonly}
          visible={visible}
          conversationId={item.conversationId}
          todo={todo}
          onUpdate={onUpdate}
        />
      ) : (
        loading && <LoadingOutlined />
      )}
    </Drawer>
  );
}

function DrawerContent({
  readonly,
  conversationId,
  todo,
  visible,
  onUpdate,
}: {
  readonly?: boolean;
  visible?: boolean;
  conversationId: string;
  todo: Todo.Record;
  onUpdate?: () => void;
}) {
  const isLightChatLayout = useIsLightChatLayout();

  const { loading, conversation } = useSecondConversation({
    conversationId,
    readonly,
    visible,
    forceAcquireControlReason: 'PROCESS_TODO',
  });

  if (!conversation) {
    return <Content>{loading && <LoadingOutlined />}</Content>;
  }

  const content = (
    <Content>
      <ChatErrorBoundary>
        <div className={chatCls}>
          {isLightChatLayout ? (
            <LightChatHeader className="h-[48px] flex-shrink-0" conversation={conversation} />
          ) : (
            <ClassicConversationHead conversation={conversation} />
          )}
          <Chat conversationId={conversationId} forceAcquireControlReason="PROCESS_TODO" />
        </div>
      </ChatErrorBoundary>

      <TodoSider
        conversation={conversation}
        todo={todo}
        readonly={readonly}
        onUpdate={onUpdate}
        isLightChatLayout={isLightChatLayout}
      />
    </Content>
  );

  return (
    <ChatConversationProvider conversationId={conversationId}>{content}</ChatConversationProvider>
  );
}

const Content = styled.div`
  display: flex;
  align-items: stretch;
  height: 100%;
  > aside {
    --main-color: var(--fett-app-primary-color);
    flex: 0 0 504px;
    overflow: hidden;
  }
`;

const chatCls = css`
  flex: 1 0 0;
  border-right: 1px solid #d3d7e0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export default TodoDrawer;
